// The glossary, read by every page that shows prose or scripture and saved
// whole by the admin.
import { termMatcher, type GlossaryEntryView, type GlossaryInput, type TermMatcher } from "@bibleunleashed/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, put } from "./api";

const KEY = ["glossary"];

export function useGlossary() {
  return useQuery({ queryKey: KEY, queryFn: () => api<GlossaryEntryView[]>("/api/glossary"), staleTime: 5 * 60_000 });
}

export function useSaveGlossary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GlossaryInput) => put<GlossaryEntryView[]>("/api/glossary", body),
    onSuccess: (saved) => qc.setQueryData(KEY, saved),
  });
}

// One matcher per glossary, shared by everything that asks. The chapter page
// repaints highlights when it changes, and it has to change exactly when the
// matcher each `Passage` rebuilt its verses with did.
const matchers = new WeakMap<GlossaryEntryView[], TermMatcher | null>();

/** The glossary's words, ready to find in text; null until it has loaded, or when it is empty. */
export function useTermMatcher(): TermMatcher | null {
  const { data } = useGlossary();
  if (!data) return null;
  if (!matchers.has(data)) matchers.set(data, termMatcher(data));
  return matchers.get(data) ?? null;
}
